import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

const typeOptions = [
  { label: 'Receitas', value: 'CREDITO' },
  { label: 'Despesas', value: 'DEBITO' },
]

const categoryOptions = [
  { label: 'Empresa', value: 'EMPRESA' },
  { label: 'Pessoal', value: 'PESSOAL' },
  { label: 'Outros', value: 'OUTROS' },
]

const selectStyles = {
  bg: 'gray.700',
  color: 'white',
  borderColor: 'gray.600',
  _hover: { borderColor: 'pink.400' },
  _focus: { borderColor: 'pink.400', boxShadow: 'none', color: 'white' },
  sx: { option: { color: 'black', backgroundColor: 'white' } },
} as const

type CreateFormData = any
type UpdateFormData = any

type FormValues = any

// Schema de validação
const createSchema = () => yup.object().shape({
  type: yup.string().required('Selecione o tipo do lançamento'),
  category: yup.string().required('Selecione a categoria'),
  document_number: yup.string().nullable(),
  movement_date: yup.string().required('Informe a data de movimento'),
  due_date: yup
    .string()
    .nullable()
    .when('type', {
      is: 'DEBITO',
      then: (schema) => schema.required('Informe a data de vencimento'),
      otherwise: (schema) => schema.nullable(),
    }),
  value: yup
    .number()
    .typeError('Informe um valor válido')
    .positive('O valor deve ser maior que zero')
    .required('Informe o valor do lançamento'),
  is_recurring: yup.boolean().default(false),
  recurrence: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .when('is_recurring', {
      is: true,
      then: (schema) => {
        // Exigir recorrência quando is_recurring for true (tanto em criação quanto em edição)
        return schema
          .typeError('Informe a quantidade de meses')
          .min(0, 'A recorrência deve ser 0 (indeterminado) ou um número positivo')
          .max(120, 'A recorrência deve ser de no máximo 120 meses')
          .required('Informe a quantidade de meses')
      },
      otherwise: (schema) => schema.notRequired(),
    }),
  client_id: yup
    .mixed()
    .nullable()
    .transform((_value, originalValue) => {
      // Se for string vazia, undefined ou null, retornar null
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      // Tentar converter para número
      const num = Number(originalValue)
      // Se for NaN ou menor/igual a zero, retornar null
      if (isNaN(num) || num <= 0) {
        return null
      }
      return num
    })
    .test('is-number-or-null', 'Cliente inválido', (value) => {
      // Aceitar null ou número válido
      return value === null || (typeof value === 'number' && !isNaN(value) && value > 0)
    })
    .nullable(),
  provider_id: yup
    .mixed()
    .nullable()
    .transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      const num = Number(originalValue)
      if (isNaN(num) || num <= 0) {
        return null
      }
      return num
    })
    .test('is-number-or-null', 'Fornecedor inválido', (value) => {
      return value === null || (typeof value === 'number' && !isNaN(value) && value > 0)
    })
    .nullable(),
  employee_id: yup
    .mixed()
    .nullable()
    .transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      const num = Number(originalValue)
      if (isNaN(num) || num <= 0) {
        return null
      }
      return num
    })
    .test('is-number-or-null', 'Funcionário inválido', (value) => {
      return value === null || (typeof value === 'number' && !isNaN(value) && value > 0)
    })
    .nullable(),
  payment_method_id: yup
    .mixed()
    .nullable()
    .transform((_value, originalValue) => {
      // Se for string vazia, undefined ou null, retornar null
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      // Tentar converter para número
      const num = Number(originalValue)
      // Se for NaN ou menor/igual a zero, retornar null
      if (isNaN(num) || num <= 0) {
        return null
      }
      return num
    })
    .test('is-number-or-null', 'Forma de pagamento inválida', (value) => {
      // Aceitar null ou número válido
      return value === null || (typeof value === 'number' && !isNaN(value) && value > 0)
    })
    .nullable(),
  payment_condition_id: yup
    .mixed()
    .nullable()
    .transform((_value, originalValue) => {
      // Se for string vazia, undefined ou null, retornar null
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      // Tentar converter para número
      const num = Number(originalValue)
      // Se for NaN ou menor/igual a zero, retornar null
      if (isNaN(num) || num <= 0) {
        return null
      }
      return num
    })
    .test('is-number-or-null', 'Condição de pagamento inválida', (value) => {
      // Aceitar null ou número válido
      return value === null || (typeof value === 'number' && !isNaN(value) && value > 0)
    })
    .nullable(),
  observation: yup.string().nullable(),
})

interface TransactionFormModalProps {
  isOpen: boolean
  mode: 'create' | 'edit'
  initialData?: any
  onSubmit: (payload: any) => Promise<void>
  onStopRecurrence?: (id: number) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  clientsOptions: { label: string; value: number }[]
  providerOptions: { label: string; value: number }[]
  employeeOptions: { label: string; value: number }[]
  paymentMethodOptions: { label: string; value: number }[]
  paymentConditionOptions: { label: string; value: number; recurrence: boolean }[]
}

export function TransactionFormModal({
  isOpen,
  mode,
  initialData,
  onSubmit,
  onStopRecurrence,
  onClose,
  isSubmitting,
  clientsOptions,
  providerOptions,
  employeeOptions,
  paymentMethodOptions,
  paymentConditionOptions,
}: TransactionFormModalProps) {
  const toast = useToast()
  const isEditMode = mode === 'edit'
  // Se não tiver id_os, é um lançamento manual e pode ser editado
  const isAutomaticLaunch = Boolean(initialData?.id_os)
  const canEditFields = !isEditMode || !isAutomaticLaunch

  const defaultValues: any = useMemo(
    () => ({
      type: '',
      category: 'EMPRESA',
      document_number: '',
      movement_date: new Date().toISOString().split('T')[0], // Data atual
      due_date: '',
      value: '',
      is_recurring: false,
      recurrence: '',
      client_id: '',
      provider_id: '',
      employee_id: '',
      payment_method_id: '',
      payment_condition_id: '',
      observation: '',
    }),
    [],
  )

  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(createSchema()),
    defaultValues,
  })

  const selectedType = watch('type')
  const isRecurring = watch('is_recurring')
  const clientId = watch('client_id')
  const providerId = watch('provider_id')
  const employeeId = watch('employee_id')
  const paymentMethodValue = watch('payment_method_id') ?? ''
  const paymentConditionValue = watch('payment_condition_id') ?? ''

  // Regras de exclusão mútua
  const isClientDisabled = Boolean(providerId) || Boolean(employeeId)
  const isProviderDisabled = Boolean(clientId) || Boolean(employeeId)
  const isEmployeeDisabled = Boolean(clientId) || Boolean(providerId)

  useEffect(() => {
    if (selectedType) {
      trigger('due_date')
    }
  }, [selectedType, trigger])

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        reset({
          type: initialData.type,
          category: initialData.category,
          document_number: initialData.document_number,
          movement_date: initialData.movement_date?.slice(0, 10) ?? '',
          due_date: initialData.due_date?.slice(0, 10) ?? '',
          value: String(initialData.value ?? ''),
          is_recurring: initialData.is_recurring ?? true,
          recurrence:
            initialData.recurrence_total ?? initialData.recurrence ?? '',
          client_id: initialData.client_id ? String(initialData.client_id) : '',
          provider_id: initialData.provider_id ? String(initialData.provider_id) : '',
          employee_id: initialData.employee_id ? String(initialData.employee_id) : '',
          payment_method_id: initialData.payment_method_id ? String(initialData.payment_method_id) : '',
          payment_condition_id: initialData.payment_condition_id
            ? String(initialData.payment_condition_id)
            : '',
          observation: initialData.observation ?? '',
        })
      } else {
        reset(defaultValues)
      }
    }
  }, [isOpen, mode, initialData, reset, defaultValues])

  const handleFormSubmit = async (values: any) => {
    try {
      const parsedValue = Number(values.value)
      // Parsear recorrência: se is_recurring for true e o campo estiver preenchido, converter para número
      let parsedRecurrence: number | undefined = undefined
      if (values.is_recurring === true) {
        if (values.recurrence !== '' && values.recurrence !== undefined && values.recurrence !== null) {
          const numValue = Number(values.recurrence)
          if (!isNaN(numValue) && numValue >= 0) {
            parsedRecurrence = numValue
          }
        }
      }

      if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        toast({ description: 'Informe um valor válido para o lançamento.', status: 'error' })
        return
      }

      // Tratar client_id: se for string vazia ou undefined, enviar null
      const parsedClientId = values.client_id && values.client_id !== '' 
        ? Number(values.client_id) 
        : null
      
      // Validar se clientId é um número válido (não NaN)
      if (parsedClientId !== null && (isNaN(parsedClientId) || parsedClientId <= 0)) {
        toast({ description: 'Cliente inválido. Selecione um cliente válido ou deixe em branco.', status: 'error' })
        return
      }

      // Tratar provider_id
      const parsedProviderId = values.provider_id && values.provider_id !== '' 
        ? Number(values.provider_id) 
        : null
      
      if (parsedProviderId !== null && (isNaN(parsedProviderId) || parsedProviderId <= 0)) {
        toast({ description: 'Fornecedor inválido. Selecione um fornecedor válido ou deixe em branco.', status: 'error' })
        return
      }

      // Tratar employee_id
      const parsedEmployeeId = values.employee_id && values.employee_id !== '' 
        ? Number(values.employee_id) 
        : null
      
      if (parsedEmployeeId !== null && (isNaN(parsedEmployeeId) || parsedEmployeeId <= 0)) {
        toast({ description: 'Funcionário inválido. Selecione um funcionário válido ou deixe em branco.', status: 'error' })
        return
      }

      const payloadBase: any = {
        type: values.type,
        category: values.category,
        document_number: values.document_number?.trim() || null,
        movement_date: values.movement_date,
        due_date: values.due_date || null,
        value: parsedValue,
        client_id: parsedClientId,
        provider_id: parsedProviderId,
        employee_id: parsedEmployeeId,
        payment_method_id: values.payment_method_id ? Number(values.payment_method_id) : null,
        payment_condition_id: values.payment_condition_id ? Number(values.payment_condition_id) : null,
        observation: values.observation?.trim() || null,
        is_recurring: !!values.is_recurring,
      }

      if (mode === 'create') {
        await onSubmit({
          mode: 'create',
          data: {
            ...payloadBase,
            recurrence: values.is_recurring ? parsedRecurrence ?? 0 : undefined,
          },
        })
      } else if (mode === 'edit' && initialData) {
        const editPayload: any = { ...payloadBase }
        // Se is_recurring for true, incluir recurrence (usar 0 como padrão se não foi preenchido)
        if (values.is_recurring) {
          editPayload.recurrence = parsedRecurrence ?? 0
        }
        
        await onSubmit({
          mode: 'edit',
          id: initialData.recurring_transaction_id ?? initialData.id,
          data: editPayload,
        })
      }
    } catch (error) {
      console.error('[TransactionFormModal] submit error', error)
      toast({ description: 'Não foi possível salvar o lançamento.', status: 'error' })
    }
  }

  const handleStopRecurrence = async () => {
    const targetId = initialData?.recurring_transaction_id ?? initialData?.id
    if (!targetId || !onStopRecurrence) return
    try {
      await onStopRecurrence(targetId)
      toast({ description: 'Recorrência encerrada com sucesso.', status: 'success' })
    } catch (error) {
      console.error('[TransactionFormModal] stop recurrence error', error)
      toast({ description: 'Não foi possível encerrar a recorrência.', status: 'error' })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800">
        <ModalHeader>{mode === 'create' ? 'Novo lançamento' : 'Editar lançamento'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="financial-recurring-form" onSubmit={handleSubmit(handleFormSubmit)}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isInvalid={!!errors.type}>
                <FormLabel>Tipo</FormLabel>
                <Select
                  placeholder="Selecione"
                  {...register('type')}
                  {...selectStyles}
                  isDisabled={!canEditFields}
                >
                  {typeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.category}>
                <FormLabel>Categoria</FormLabel>
                <Select
                  placeholder="Selecione"
                  {...register('category')}
                  {...selectStyles}
                  isDisabled={!canEditFields}
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isInvalid={!!errors.document_number}>
                <FormLabel>Número do documento</FormLabel>
                <Input placeholder="Ex: NF-12345" {...register('document_number')} isDisabled={!canEditFields} />
                <FormErrorMessage>{errors.document_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.value}>
                <FormLabel>Valor</FormLabel>
                <Input type="number" step="0.01" placeholder="0.00" {...register('value')} />
                <FormErrorMessage>{errors.value?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isInvalid={!!errors.movement_date}>
                <FormLabel>Data de movimento</FormLabel>
                <Input type="date" {...register('movement_date')} isDisabled={!canEditFields} />
                <FormErrorMessage>{errors.movement_date?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.due_date}>
                <FormLabel>Data de vencimento</FormLabel>
                <Input type="date" {...register('due_date')} isDisabled={!canEditFields} />
                <FormErrorMessage>{errors.due_date?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isInvalid={!!errors.client_id}>
                <FormLabel>Cliente (opcional)</FormLabel>
                <Select
                  placeholder="Selecione (opcional)"
                  {...register('client_id', {
                    onChange: (e) => {
                      // Limpar provider e employee quando cliente for selecionado
                      if (e.target.value) {
                        setValue('provider_id', '')
                        setValue('employee_id', '')
                      }
                    },
                  })}
                  {...selectStyles}
                  width="100%"
                  isDisabled={!canEditFields || isClientDisabled}
                >
                  <option value="">Nenhum</option>
                  {clientsOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.client_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.provider_id}>
                <FormLabel>Fornecedor (opcional)</FormLabel>
                <Select
                  placeholder="Selecione (opcional)"
                  {...register('provider_id', {
                    onChange: (e) => {
                      // Limpar client e employee quando provider for selecionado
                      if (e.target.value) {
                        setValue('client_id', '')
                        setValue('employee_id', '')
                      }
                    },
                  })}
                  {...selectStyles}
                  width="100%"
                  isDisabled={!canEditFields || isProviderDisabled}
                >
                  <option value="">Nenhum</option>
                  {providerOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.provider_id?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isInvalid={!!errors.employee_id}>
                <FormLabel>Funcionário (opcional)</FormLabel>
                <Select
                  placeholder="Selecione (opcional)"
                  {...register('employee_id', {
                    onChange: (e) => {
                      // Limpar client e provider quando employee for selecionado
                      if (e.target.value) {
                        setValue('client_id', '')
                        setValue('provider_id', '')
                      }
                    },
                  })}
                  {...selectStyles}
                  width="100%"
                  isDisabled={!canEditFields || isEmployeeDisabled}
                >
                  <option value="">Nenhum</option>
                  {employeeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.employee_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Forma de pagamento (opcional)</FormLabel>
                <Select
                  placeholder="Selecione (opcional)"
                  {...register('payment_method_id')}
                  {...selectStyles}
                  width="100%"
                  value={paymentMethodValue}
                  isDisabled={!canEditFields}>
                  {paymentMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <FormControl>
              <FormLabel>Condição de pagamento (opcional)</FormLabel>
              <Select
                placeholder="Selecione (opcional)"
                {...register('payment_condition_id')}
                {...selectStyles}
                width="100%"
                value={paymentConditionValue}
                isDisabled={!canEditFields}>
                {paymentConditionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Flex h="full" align="center" pt={{ base: 2, md: 6 }}>
                <Checkbox colorScheme="pink" {...register('is_recurring')} isDisabled={!canEditFields}>
                  Registrar como recorrente
                </Checkbox>
              </Flex>
            </FormControl>
            </SimpleGrid>

          {isRecurring && (
            <FormControl mb={4} isInvalid={!!errors.recurrence}>
              <FormLabel>Recorrência (meses)</FormLabel>
              <Input 
                type="number" 
                min="0" 
                max="120" 
                placeholder="0 = tempo indeterminado" 
                {...register('recurrence')}
                isDisabled={!canEditFields}
              />
              <Text fontSize="sm" color="gray.400" mt={2}>
                Use 0 para recorrências sem prazo final. Valores positivos definem a quantidade de meses.
              </Text>
              <FormErrorMessage>{errors.recurrence?.message}</FormErrorMessage>
            </FormControl>
          )}

          {mode === 'edit' && !isRecurring && initialData?.recurrence_total !== undefined && (
            <Box mb={4}>
              <Text fontWeight="semibold">Recorrência:</Text>
              <Text color="gray.300">
                {initialData.sequence ?? 1}/
                {initialData.recurrence_total === null || initialData.recurrence_total === undefined
                  ? '∞'
                  : initialData.recurrence_total}
              </Text>
            </Box>
          )}

            <FormControl>
              <FormLabel>Observação</FormLabel>
              <Textarea rows={3} {...register('observation')} />
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter flexDir={{ base: 'column', md: 'row' }} alignItems={{ base: 'stretch', md: 'center' }}>
          {mode === 'edit' && initialData?.is_recurring && onStopRecurrence && (
            <Button
              variant="outline"
              colorScheme="red"
              mr={{ base: 0, md: 'auto' }}
              mb={{ base: 3, md: 0 }}
              onClick={handleStopRecurrence}
            >
              Encerrar recorrência
            </Button>
          )}
          <Flex w={{ base: '100%', md: 'auto' }} justify="flex-end">
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="pink" type="submit" form="financial-recurring-form" isLoading={isSubmitting}>
              Salvar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
